// Contracts for the aerodrome data of #81: what plan.aerodromes holds (the
// JSONB shapes are validated here, nowhere else), what the API returns, and
// the AIP snapshot an admin uploads. The reader that fills these from the
// eAIP is apps/api/src/domain/aerodromes/aipPage.ts.

import { z } from 'zod'

export const FREQUENCY_KINDS = ['twr', 'gnd', 'app', 'atis', 'info', 'afis', 'ctaf', 'glider', 'other'] as const
export type FrequencyKind = (typeof FREQUENCY_KINDS)[number]

export const RunwaySchema = z.object({
  // "04/22", or "04L/22R": both ends, as the source writes them.
  designator: z.string().trim().min(1).max(12),
  // True and magnetic heading of each end, in the designator's order.
  headingTrue: z.tuple([z.number().min(0).max(360), z.number().min(0).max(360)]).nullable(),
  headingMag: z.tuple([z.number().min(0).max(360), z.number().min(0).max(360)]).nullable(),
  lengthM: z.number().int().positive().max(10000).nullable(),
  widthM: z.number().int().positive().max(500).nullable(),
  surface: z.string().trim().max(60).nullable(),
})
export type Runway = z.infer<typeof RunwaySchema>

export const FrequencySchema = z.object({
  kind: z.enum(FREQUENCY_KINDS),
  // Kept as text: 8.33 kHz channels such as "118.305" and legacy "123,150"
  // are both written exactly as published, normalised to a dot.
  mhz: z.string().regex(/^\d{3}\.\d{2,3}$/),
  callsign: z.string().trim().max(60).nullable().optional(),
})
export type Frequency = z.infer<typeof FrequencySchema>

export const AerodromeLinkSchema = z.object({
  label: z.string().trim().min(1).max(200),
  url: z.string().url().regex(/^https?:\/\//i).max(500),
})
export type AerodromeLink = z.infer<typeof AerodromeLinkSchema>

export const AerodromeDetailsSchema = z.object({
  runways: z.array(RunwaySchema).max(8),
  frequencies: z.array(FrequencySchema).max(20),
  links: z.array(AerodromeLinkSchema).max(30),
  details: z.record(z.string(), z.unknown()).default({}),
})

// A visual reporting point, or a published fix, of one aerodrome.
export const AerodromePointSchema = z.object({
  // The part after the aerodrome's code: "POHJOINEN", not "EFNU-POHJOINEN".
  name: z.string().trim().min(1).max(40),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
})
export type AerodromePoint = z.infer<typeof AerodromePointSchema>

export const AerodromeRecordSchema = AerodromeDetailsSchema.extend({
  code: z.string().trim().toUpperCase().min(1).max(20),
  icao: z.string().trim().toUpperCase().length(4).nullable(),
  name: z.string().trim().max(100).nullable(),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  elevationFt: z.number().int().min(-1500).max(30000).nullable(),
  controlled: z.boolean(),
  sourceUrl: z.string().url().nullable(),
  vrps: z.array(AerodromePointSchema).max(30),
})
export type AerodromeRecord = z.infer<typeof AerodromeRecordSchema>

// The eAIPs the planner is built on: Finland's (the original, so its key is
// plain 'aip'), Estonia's and Sweden's (#157). All three follow the ICAO
// Annex 15 eAIP layout, with differences in markup that the page readers
// absorb (see aipPage.ts).
export const DATA_SOURCES = ['aip', 'aip-ee', 'aip-se'] as const
export type DataSourceKey = (typeof DATA_SOURCES)[number]

export const AIP_SOURCES: Record<DataSourceKey, { country: 'fi' | 'ee' | 'se'; icaoPrefix: string; label: string }> = {
  aip: { country: 'fi', icaoPrefix: 'EF', label: 'eAIP Finland' },
  'aip-ee': { country: 'ee', icaoPrefix: 'EE', label: 'eAIP Estonia' },
  'aip-se': { country: 'se', icaoPrefix: 'ES', label: 'eAIP Sweden' },
}

// The country an aerodrome's ICAO location indicator belongs to (EF.. is
// Finland, EE.. Estonia, ES.. Sweden); Finland for anything else, as every
// waypoint was before #157.
export function countryOfIcao(icao: string | null | undefined): 'fi' | 'ee' | 'se' {
  const prefix = (icao ?? '').slice(0, 2).toUpperCase()
  return Object.values(AIP_SOURCES).find((s) => s.icaoPrefix === prefix)?.country ?? 'fi'
}

// What an admin uploads after the AIP update skill has read an edition: every
// aerodrome in its AD 2, controlled or not.
export const AipSnapshotSchema = z.object({
  // Which eAIP it is from; snapshots made before #157 name none and are Finland's.
  source: z.enum(DATA_SOURCES).default('aip'),
  // "01 OCT 2026_2026_10_01": the eAIP edition folder.
  revision: z.string().trim().min(1).max(60),
  effectiveAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  aerodromes: z.array(AerodromeRecordSchema).min(1).max(200),
})
export type AipSnapshot = z.infer<typeof AipSnapshotSchema>

// db/generate/aerodromes/lentopaikat.json: what lentopaikat.fi says, read by a
// script run by hand, merged under the AIP's data when the snapshot is made.
export const LentopaikatSupplementSchema = z.object({
  retrievedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  aerodromes: z.array(AerodromeRecordSchema).max(300),
})
export type LentopaikatSupplement = z.infer<typeof LentopaikatSupplementSchema>

export const PENDING_STATUSES = ['awaiting_ai', 'staged', 'applied', 'failed'] as const
export type PendingStatus = (typeof PENDING_STATUSES)[number]

// The runway end a designator's number belongs to: "04/22" is 040° and 220°
// nominally, "04L/22R" the same. Used only when a source gives no heading.
export function nominalHeadings(designator: string): [number, number] | null {
  const m = /^(\d{2})[LRC]?\/(\d{2})[LRC]?$/i.exec(designator.trim())
  if (!m) return null
  const [a, b] = [Number(m[1]), Number(m[2])]
  if (a < 1 || a > 36 || b < 1 || b > 36) return null
  return [a * 10 === 360 ? 360 : a * 10, b * 10 === 360 ? 360 : b * 10]
}

// What GET /api/plan/aerodromes/:code returns: the waypoint, the details
// and the VRPs that belong to it.
export interface AerodromeDetail {
  code: string
  name: string | null
  lat: number
  lon: number
  variation: number | null
  icao: string | null
  controlled: boolean
  elevationFt: number | null
  runways: Runway[]
  frequencies: Frequency[]
  links: AerodromeLink[]
  details: Record<string, unknown>
  sourceRevision: string | null
  sourceUrl: string | null
  updatedAt: string
  vrps: { code: string; name: string | null; lat: number; lon: number; variation: number | null }[]
}

// What GET /api/plan/data-sources returns per source.
export interface DataSourceStatus {
  source: DataSourceKey
  revision: string | null
  effectiveAt: string | null
  lastCheckedAt: string | null
  lastChangedAt: string | null
  airspaceCycle: string | null
}
